/**
 * Arm Shark Shopify PDP interactions.
 *
 * Portable by design: this module uses only browser APIs and documented
 * Shopify product data rendered by the Liquid snippets. It does not depend on
 * Dawn custom elements, global theme helpers, jQuery, or third-party apps.
 */
(function armSharkProductPage() {
  'use strict';

  var ROOT_SELECTOR = '.arm-shark-pdp';
  var MAIN_PRODUCT_SELECTOR = '[data-as-main-product], [data-arm-shark-product]';
  var PRODUCT_ROOT_SELECTOR = '[data-as-product-root], [data-arm-shark-product-form]';
  var MOBILE_ATC_SELECTOR = '[data-as-mobile-atc], [data-arm-shark-mobile-atc]';
  var VIDEO_SELECTOR = '[data-as-video], [data-arm-shark-video]';
  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var saveData = Boolean(navigator.connection && navigator.connection.saveData);

  function each(collection, callback) {
    Array.prototype.forEach.call(collection || [], callback);
  }

  function queryAny(scope, selectors) {
    return scope.querySelector(selectors);
  }

  function getRoots(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = [];

    if (context.matches && context.matches(ROOT_SELECTOR)) roots.push(context);
    each(context.querySelectorAll(ROOT_SELECTOR), function pushRoot(root) {
      if (roots.indexOf(root) === -1) roots.push(root);
    });

    return roots;
  }

  function pauseMedia(mediaItem) {
    if (!mediaItem) return;
    each(mediaItem.querySelectorAll('video, audio'), function pauseElement(media) {
      if (!media.paused) media.pause();
    });
  }

  function selectGalleryMedia(gallery, id, focusStage) {
    if (!gallery || id === null || typeof id === 'undefined') return;
    var targetId = String(id);
    var mediaItems = gallery.querySelectorAll('[data-as-media-id], [data-arm-shark-media]');
    var controls = gallery.querySelectorAll('[data-as-media-target], [data-arm-shark-gallery-target]');
    var selectedItem = null;

    each(mediaItems, function toggleMedia(item) {
      var itemId = item.getAttribute('data-as-media-id') || item.getAttribute('data-arm-shark-media');
      var active = String(itemId) === targetId;
      if (!active) pauseMedia(item);
      item.hidden = !active;
      item.classList.toggle('is-active', active);
      if (active) selectedItem = item;
    });

    each(controls, function toggleControl(control) {
      var controlId = control.getAttribute('data-as-media-target') || control.getAttribute('data-arm-shark-gallery-target');
      var active = String(controlId) === targetId;
      control.classList.toggle('is-active', active);
      control.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (focusStage && selectedItem) {
      var focusable = selectedItem.querySelector('button, video[controls], model-viewer, a, iframe');
      if (focusable) focusable.focus({ preventScroll: true });
    }
  }

  function initializeGallery(gallery) {
    if (!gallery || gallery.dataset.asInitialized === 'true') return;
    gallery.dataset.asInitialized = 'true';

    each(gallery.querySelectorAll('[data-as-media-target], [data-arm-shark-gallery-target]'), function bindThumb(button) {
      button.addEventListener('click', function onThumbClick() {
        var mediaId = button.getAttribute('data-as-media-target') || button.getAttribute('data-arm-shark-gallery-target');
        selectGalleryMedia(gallery, mediaId, false);
      });

      button.addEventListener('keydown', function onThumbKeydown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        var buttons = Array.prototype.slice.call(
          gallery.querySelectorAll('[data-as-media-target], [data-arm-shark-gallery-target]')
        );
        var index = buttons.indexOf(button);
        var direction = event.key === 'ArrowRight' ? 1 : -1;
        var next = buttons[(index + direction + buttons.length) % buttons.length];
        if (!next) return;
        event.preventDefault();
        next.focus();
        next.click();
      });
    });
  }

  function playFullDemo(player, container) {
    try {
      if (player && typeof player.play === 'function') {
        player.play();
        return true;
      }
      if (player && player.player && typeof player.player.play === 'function') {
        player.player.play();
        return true;
      }

      var playerButton = container && container.querySelector(
        '[data-vid-testid="controls:big-play-button"], button[aria-label="Play"]'
      );
      if (playerButton) {
        playerButton.click();
        return true;
      }

      var nativeVideo = container && container.querySelector('video');
      if (nativeVideo) {
        nativeVideo.play();
        return true;
      }
    } catch (error) {
      console.warn('[Arm Shark] Full demonstration playback needs a second tap.', error);
    }

    return false;
  }

  function initializeDemoLaunches(root) {
    each(root.querySelectorAll('[data-as-demo-launch]'), function bindDemoLaunch(button) {
      if (button.dataset.asDemoInitialized === 'true') return;
      button.dataset.asDemoInitialized = 'true';

      button.addEventListener('click', function launchFullDemo() {
        var targetSelector = button.getAttribute('data-as-demo-launch') || '#ArmSharkFullDemo';
        var fullDemo = document.querySelector(targetSelector);
        if (!fullDemo) return;

        playFullDemo(window.ArmSharkVidalyticsPlayer, fullDemo);
        fullDemo.scrollIntoView({
          behavior: reduceMotionQuery.matches ? 'auto' : 'smooth',
          block: 'center'
        });
        fullDemo.focus({ preventScroll: true });

        if (!window.ArmSharkVidalyticsPlayer) {
          document.addEventListener('armshark:full-demo-ready', function onDemoReady(event) {
            playFullDemo(event.detail && event.detail.player, fullDemo);
          }, { once: true });
        }
      });
    });
  }

  function selectedOptions(productRoot) {
    var pickers = Array.prototype.slice.call(productRoot.querySelectorAll('[data-as-option-position]'));
    pickers.sort(function sortPickers(a, b) {
      return Number(a.getAttribute('data-as-option-position')) - Number(b.getAttribute('data-as-option-position'));
    });

    return pickers.map(function getSelected(picker) {
      var checked = picker.querySelector('[data-as-option-input]:checked');
      return checked ? checked.value : null;
    });
  }

  function parseVariants(productRoot) {
    var script = queryAny(productRoot, '[data-as-variants-json], [data-arm-shark-variants-json]');
    if (!script) return [];

    try {
      var parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[Arm Shark] Variant data could not be parsed.', error);
      return [];
    }
  }

  function activeCurrency(productRoot) {
    return productRoot.getAttribute('data-currency') ||
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD';
  }

  function formatMoney(cents, productRoot) {
    var amount = Number(cents || 0) / 100;
    var locale = document.documentElement.lang || navigator.language || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency(productRoot),
        currencyDisplay: 'narrowSymbol'
      }).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(2).replace(/\.00$/, '');
    }
  }

  function updatePrice(mainProduct, productRoot, variant) {
    var price = queryAny(mainProduct, '[data-as-current-price], [data-arm-shark-price]');
    var compare = queryAny(mainProduct, '[data-as-compare-price], [data-arm-shark-compare-price]');
    var saleBadge = queryAny(mainProduct, '[data-as-sale-badge]');
    var soldOutBadge = queryAny(mainProduct, '[data-as-sold-out-badge]');
    var isOnSale = Boolean(variant && variant.compare_at_price && variant.compare_at_price > variant.price);

    if (price && variant) price.textContent = formatMoney(variant.price, productRoot);
    if (compare) {
      compare.textContent = isOnSale ? formatMoney(variant.compare_at_price, productRoot) : '';
      compare.classList.toggle('is-hidden', !isOnSale);
    }
    if (saleBadge) saleBadge.classList.toggle('is-hidden', !isOnSale);
    if (soldOutBadge) soldOutBadge.classList.toggle('is-hidden', Boolean(variant && variant.available));

    var mobilePriceSelector = [
      '[data-as-mobile-atc] [data-as-mobile-price]',
      '[data-as-mobile-atc] [data-arm-shark-mobile-price]',
      '[data-arm-shark-mobile-atc] [data-as-mobile-price]',
      '[data-arm-shark-mobile-atc] [data-arm-shark-mobile-price]'
    ].join(', ');
    each(document.querySelectorAll(mobilePriceSelector), function updateMobilePrice(node) {
      if (variant) node.textContent = formatMoney(variant.price, productRoot);
    });
  }

  function updateInventory(mainProduct, variant) {
    var inventory = queryAny(mainProduct, '[data-as-inventory], [data-arm-shark-inventory]');
    if (!inventory || !variant) return;

    var dot = '<span class="as-pdp__inventory-dot" aria-hidden="true"></span>';
    if (!variant.available) {
      inventory.textContent = 'Currently sold out';
      return;
    }

    inventory.innerHTML = dot + 'Limited first production run';
  }

  function updateVariantForm(mainProduct, productRoot, variant) {
    var variantInput = queryAny(productRoot, '[data-as-variant-id], [data-arm-shark-variant]');
    var addButton = queryAny(productRoot, '[data-as-add-to-cart], [data-arm-shark-add-to-cart]');
    var addLabel = addButton && queryAny(addButton, '[data-as-add-label], [data-arm-shark-add-label]');
    var dynamicCheckout = queryAny(productRoot, '[data-as-dynamic-checkout]');
    var available = Boolean(variant && variant.available);

    if (variantInput) {
      variantInput.value = variant ? variant.id : '';
      variantInput.disabled = !available;
    }

    if (addButton) {
      if (!addButton.dataset.asAvailableLabel) {
        addButton.dataset.asAvailableLabel = addLabel ? addLabel.textContent.trim() : addButton.textContent.trim();
      }
      addButton.disabled = !available;
      addButton.setAttribute('aria-disabled', available ? 'false' : 'true');
      if (addLabel) addLabel.textContent = available ? addButton.dataset.asAvailableLabel : 'Sold out';
    }

    if (dynamicCheckout) dynamicCheckout.hidden = !available;

    each(document.querySelectorAll(MOBILE_ATC_SELECTOR), function updateMobileBar(bar) {
      var mobileButton = queryAny(bar, '[data-as-mobile-submit], [data-arm-shark-mobile-atc-submit], .as-mobile-atc__button');
      if (!mobileButton) return;
      mobileButton.disabled = !available;
      mobileButton.setAttribute('aria-disabled', available ? 'false' : 'true');
      if (mobileButton.dataset.asAvailableLabel === undefined) {
        mobileButton.dataset.asAvailableLabel = mobileButton.textContent.trim();
      }
      mobileButton.textContent = available ? mobileButton.dataset.asAvailableLabel : 'Sold out';
    });

    updatePrice(mainProduct, productRoot, variant);
    updateInventory(mainProduct, variant);
  }

  function variantForOptions(variants, options) {
    return variants.find(function findVariant(variant) {
      return options.every(function optionMatches(value, index) {
        return value === null || String(variant.options[index]) === String(value);
      });
    });
  }

  function updateSelectedOptionLabels(productRoot) {
    each(productRoot.querySelectorAll('[data-as-option-position]'), function updatePicker(picker) {
      var checked = picker.querySelector('[data-as-option-input]:checked');
      var output = picker.querySelector('[data-as-selected-option]');
      if (checked && output) output.textContent = checked.value;
    });
  }

  function updateVariantUrl(productRoot, variant) {
    if (!variant || productRoot.getAttribute('data-update-url') === 'false') return;
    if (!window.history || !window.history.replaceState) return;

    var productUrl = productRoot.getAttribute('data-product-url') || window.location.pathname;
    var nextUrl = new URL(productUrl, window.location.origin);
    nextUrl.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', nextUrl.pathname + nextUrl.search + window.location.hash);
  }

  function showVariantMedia(mainProduct, variant) {
    if (!variant) return;
    var featured = variant.featured_media || variant.featured_image;
    var mediaId = featured && featured.id;
    var gallery = queryAny(mainProduct, '[data-as-media-gallery], [data-arm-shark-gallery]');
    if (gallery && mediaId) selectGalleryMedia(gallery, mediaId, false);
  }

  function initializeProduct(productRoot) {
    if (!productRoot || productRoot.dataset.asInitialized === 'true') return;
    productRoot.dataset.asInitialized = 'true';

    var mainProduct = productRoot.closest(MAIN_PRODUCT_SELECTOR) || productRoot.closest(ROOT_SELECTOR) || document;
    var variants = parseVariants(productRoot);
    var controls = productRoot.querySelectorAll('[data-as-option-input], [data-arm-shark-option-input]');

    function onOptionChange() {
      updateSelectedOptionLabels(productRoot);
      var variant = variantForOptions(variants, selectedOptions(productRoot));
      updateVariantForm(mainProduct, productRoot, variant);
      showVariantMedia(mainProduct, variant);
      updateVariantUrl(productRoot, variant);

      productRoot.dispatchEvent(new CustomEvent('armshark:variant-change', {
        bubbles: true,
        detail: { variant: variant || null }
      }));
    }

    each(controls, function bindOption(control) {
      control.addEventListener('change', onOptionChange);
    });

    if (variants.length) {
      var initialInput = queryAny(productRoot, '[data-as-variant-id], [data-arm-shark-variant]');
      var initialVariant = variants.find(function matchId(variant) {
        return initialInput && String(variant.id) === String(initialInput.value);
      }) || variants[0];
      updateVariantForm(mainProduct, productRoot, initialVariant);
    }
  }

  function initializeVideo(wrapper) {
    if (!wrapper || wrapper.dataset.asInitialized === 'true') return;

    if (wrapper.matches('video')) {
      var parentVideoWrapper = wrapper.parentElement && wrapper.parentElement.closest(VIDEO_SELECTOR);
      if (parentVideoWrapper && parentVideoWrapper !== wrapper) return;
    }

    wrapper.dataset.asInitialized = 'true';

    var video = wrapper.matches('video') ? wrapper : wrapper.querySelector('video');
    var trigger = queryAny(wrapper, '[data-as-video-trigger], [data-arm-shark-video-trigger], .as-video__trigger');
    if (!video) return;

    var decorative = wrapper.getAttribute('data-decorative') === 'true' || video.getAttribute('data-decorative') === 'true';
    if (saveData) video.preload = 'none';

    function updateVideoState() {
      var playing = !video.paused && !video.ended;
      wrapper.classList.toggle('is-playing', playing);
      if (trigger) {
        trigger.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        trigger.setAttribute('aria-pressed', playing ? 'true' : 'false');
      }
    }

    function toggleVideo() {
      if (video.paused || video.ended) {
        var promise = video.play();
        if (promise && typeof promise.catch === 'function') {
          promise.catch(function playbackRejected() {
            video.controls = true;
            updateVideoState();
          });
        }
      } else {
        video.pause();
      }
    }

    if (trigger) trigger.addEventListener('click', toggleVideo);
    video.addEventListener('play', function onPlay() {
      updateVideoState();
      wrapper.dispatchEvent(new CustomEvent('armshark:video-play', { bubbles: true }));
    });
    video.addEventListener('pause', updateVideoState);
    video.addEventListener('ended', updateVideoState);

    if (decorative) {
      video.muted = true;
      video.loop = true;
      video.controls = false;

      if (reduceMotionQuery.matches || saveData) {
        video.pause();
        video.removeAttribute('autoplay');
      } else if ('IntersectionObserver' in window) {
        var videoObserver = new IntersectionObserver(function onVideoIntersection(entries) {
          each(entries, function updatePlayback(entry) {
            if (entry.isIntersecting) {
              var playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function ignoreAutoplayBlock() {});
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.35 });
        videoObserver.observe(wrapper);
      }
    } else {
      video.removeAttribute('autoplay');
      if (!video.hasAttribute('controls') && !trigger) video.controls = true;
      if (!video.paused) video.pause();
    }

    updateVideoState();
  }

  function initializeReveals(root) {
    var items = root.querySelectorAll('[data-as-reveal], [data-arm-shark-reveal]');
    if (!items.length) return;

    if (reduceMotionQuery.matches || !('IntersectionObserver' in window)) {
      each(items, function revealImmediately(item) { item.classList.add('is-visible'); });
      return;
    }

    var observer = new IntersectionObserver(function onReveal(entries) {
      each(entries, function revealEntry(entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    each(items, function observeReveal(item) { observer.observe(item); });
  }

  function initializeAccordions(root) {
    each(root.querySelectorAll('[data-as-accordion], [data-arm-shark-accordion]'), function bindAccordion(list) {
      if (list.dataset.asInitialized === 'true') return;
      list.dataset.asInitialized = 'true';
      each(list.querySelectorAll('details'), function bindDetail(detail) {
        detail.addEventListener('toggle', function closeSiblings() {
          if (!detail.open || list.getAttribute('data-multiple') === 'true') return;
          each(list.querySelectorAll('details[open]'), function closeOther(other) {
            if (other !== detail) other.open = false;
          });
        });
      });
    });
  }

  function setMobileBarVisibility(bar, visible) {
    bar.classList.toggle('is-visible', visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if ('inert' in bar) bar.inert = !visible;
    each(bar.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'), function updateTabOrder(control) {
      control.tabIndex = visible && !control.disabled ? 0 : -1;
    });
  }

  function initializeMobileAtc() {
    var bars = document.querySelectorAll(MOBILE_ATC_SELECTOR);
    var productRoot = document.querySelector(PRODUCT_ROOT_SELECTOR);
    if (!bars.length || !productRoot) return;

    var form = queryAny(productRoot, 'form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
    var mainButton = queryAny(productRoot, '[data-as-add-to-cart], [data-arm-shark-add-to-cart]');
    var mainProduct = productRoot.closest(MAIN_PRODUCT_SELECTOR) || productRoot;

    each(bars, function bindBar(bar) {
      if (bar.dataset.asInitialized === 'true') return;
      bar.dataset.asInitialized = 'true';
      setMobileBarVisibility(bar, false);

      var stickyButton = queryAny(bar, '[data-as-mobile-submit], [data-arm-shark-mobile-atc-submit], .as-mobile-atc__button');
      if (stickyButton) {
        stickyButton.addEventListener('click', function submitMainForm(event) {
          event.preventDefault();
          if (!mainButton || mainButton.disabled) {
            mainProduct.scrollIntoView({ behavior: reduceMotionQuery.matches ? 'auto' : 'smooth', block: 'start' });
            return;
          }

          if (form && typeof form.requestSubmit === 'function') {
            form.requestSubmit(mainButton);
          } else {
            mainButton.click();
          }

          bar.dispatchEvent(new CustomEvent('armshark:sticky-atc-submit', { bubbles: true }));
        });
      }
    });

    if (!('IntersectionObserver' in window)) return;
    var formObserver = new IntersectionObserver(function onFormVisibility(entries) {
      var formVisible = entries.some(function someVisible(entry) { return entry.isIntersecting; });
      var canPurchase = Boolean(mainButton && !mainButton.disabled);
      var show = canPurchase && !formVisible && window.scrollY > mainProduct.offsetTop + 120;
      each(bars, function updateBar(bar) { setMobileBarVisibility(bar, show); });
    }, { threshold: 0.05 });
    formObserver.observe(productRoot);
  }

  function initializeFavicon(scope) {
    var context = scope && scope.querySelector ? scope : document;
    var mainProduct = context.matches && context.matches(MAIN_PRODUCT_SELECTOR)
      ? context
      : context.querySelector(MAIN_PRODUCT_SELECTOR);
    if (!mainProduct) return;

    var faviconUrl = mainProduct.getAttribute('data-as-favicon-url');
    if (!faviconUrl) return;

    var favicon = document.querySelector('link[data-arm-shark-page-favicon]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/svg+xml';
      favicon.setAttribute('sizes', 'any');
      favicon.setAttribute('data-arm-shark-page-favicon', '');
      document.head.appendChild(favicon);
    }
    favicon.href = faviconUrl;
  }

  function initializeRoot(root) {
    if (!root) return;
    root.classList.add('as-js');
    each(root.querySelectorAll('[data-as-media-gallery], [data-arm-shark-gallery]'), initializeGallery);
    initializeDemoLaunches(root);
    each(root.querySelectorAll(PRODUCT_ROOT_SELECTOR), initializeProduct);
    each(root.querySelectorAll(VIDEO_SELECTOR), initializeVideo);
    initializeReveals(root);
    initializeAccordions(root);
  }

  function initialize(scope) {
    initializeFavicon(scope);
    each(getRoots(scope), initializeRoot);
    initializeMobileAtc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function onReady() { initialize(document); }, { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', function onSectionLoad(event) {
    initialize(event.target);
  });
})();
